import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Underline, List, ListOrdered, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TextInputDialog } from './ConfirmDialog';
import { toast } from '../lib/toast';
import { isRichText } from '../lib/richText';

interface RichTextFieldProps {
  /** HTML (or an older plain-text description, shown as paragraphs). */
  value: string;
  /** HTML, or '' when the field has no text. */
  onChange: (html: string) => void;
  placeholder?: string;
  /** Maximum number of characters of text (formatting not counted). */
  maxChars?: number;
  disabled?: boolean;
  ariaLabel?: string;
}

const plainToHtml = (text: string) =>
  text
    .split(/\n{2,}|\r\n\r\n/)
    .map((para) => `<p>${para.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r?\n/g, '<br>')}</p>`)
    .join('');

/** Formatted text input for video descriptions: bold, italic, underline, lists and links. */
const RichTextField: React.FC<RichTextFieldProps> = ({
  value, onChange, placeholder, maxChars = 5000, disabled = false, ariaLabel,
}) => {
  const { t } = useTranslation();
  const [chars, setChars] = useState(0);
  // Re-render on selection changes so the toolbar shows the active formatting.
  const [, setTick] = useState(0);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, codeBlock: false, code: false, blockquote: false, horizontalRule: false,
        link: { openOnClick: false, autolink: true, protocols: ['http', 'https', 'mailto'] },
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value && !isRichText(value) ? plainToHtml(value) : value,
    editable: !disabled,
    editorProps: {
      attributes: { 'aria-label': ariaLabel ?? placeholder ?? '', role: 'textbox', 'aria-multiline': 'true' },
    },
    onCreate({ editor: e }) { setChars(e.getText().trim().length); },
    onUpdate({ editor: e }) {
      const text = e.getText().trim();
      setChars(text.length);
      onChange(text ? e.getHTML() : '');
    },
    onSelectionUpdate() { setTick((n) => n + 1); },
  });

  useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);

  const applyLink = (raw: string) => {
    setShowLinkDialog(false);
    if (!editor) return;
    // "example.com" → "https://example.com"; only web and mail links are allowed.
    const href = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
    if (!/^(https?:|mailto:)/i.test(href)) {
      toast.error(t('comment_link_invalid', 'Enter a valid web address.'));
      editor.commands.focus();
      return;
    }
    if (editor.state.selection.empty) {
      editor.chain().focus()
        .insertContent({ type: 'text', text: raw, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
  };

  const tools: { key: string; label: string; icon: React.ReactNode; active: boolean; run: () => void }[] = [
    { key: 'bold', label: t('rich_text.bold', 'Bold'), icon: <Bold size={14} />, active: !!editor?.isActive('bold'), run: () => editor?.chain().focus().toggleBold().run() },
    { key: 'italic', label: t('rich_text.italic', 'Italic'), icon: <Italic size={14} />, active: !!editor?.isActive('italic'), run: () => editor?.chain().focus().toggleItalic().run() },
    { key: 'underline', label: t('rich_text.underline', 'Underline'), icon: <Underline size={14} />, active: !!editor?.isActive('underline'), run: () => editor?.chain().focus().toggleUnderline().run() },
    { key: 'bullet', label: t('rich_text.bullet_list', 'Bulleted list'), icon: <List size={14} />, active: !!editor?.isActive('bulletList'), run: () => editor?.chain().focus().toggleBulletList().run() },
    { key: 'ordered', label: t('rich_text.ordered_list', 'Numbered list'), icon: <ListOrdered size={14} />, active: !!editor?.isActive('orderedList'), run: () => editor?.chain().focus().toggleOrderedList().run() },
    {
      key: 'link', label: t('rich_text.link', 'Link'), icon: <Link2 size={14} />, active: !!editor?.isActive('link'),
      run: () => (editor?.isActive('link') ? editor.chain().focus().extendMarkRange('link').unsetLink().run() : setShowLinkDialog(true)),
    },
  ];

  const over = chars > maxChars;

  return (
    <div
      className="rich-text-field"
      style={{
        border: `1px solid ${over ? 'var(--theme-error, #dc2626)' : 'var(--border-color, var(--theme-border, #d1d5db))'}`,
        borderRadius: 8,
        background: 'var(--bg-alternate, var(--theme-background, #fff))',
        color: 'var(--text-primary, var(--theme-foreground, #111))',
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div
        role="toolbar"
        aria-label={t('rich_text.toolbar', 'Formatting')}
        style={{
          display: 'flex', alignItems: 'center', gap: 2, padding: '4px 6px', flexWrap: 'wrap',
          borderBottom: '1px solid var(--border-color, var(--theme-border, #e5e7eb))',
        }}
      >
        {tools.map((tool) => (
          <button
            key={tool.key}
            type="button"
            onMouseDown={(e) => e.preventDefault()} // keep the selection in the editor
            onClick={tool.run}
            disabled={disabled}
            title={tool.label}
            aria-label={tool.label}
            aria-pressed={tool.active}
            style={{
              display: 'flex', alignItems: 'center', padding: '4px 6px', borderRadius: 6, border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: tool.active ? 'var(--surfaceSelected, #ede9fe)' : 'transparent',
              color: tool.active ? 'var(--surfaceSelectedForeground, #4f46e5)' : 'inherit',
            }}
          >
            {tool.icon}
          </button>
        ))}
        <span
          aria-live="polite"
          style={{ marginLeft: 'auto', fontSize: 11, opacity: over ? 1 : 0.6, color: over ? 'var(--theme-error, #dc2626)' : 'inherit' }}
        >
          {chars}/{maxChars}
        </span>
      </div>
      <EditorContent editor={editor} className="rich-text-field__content" />

      {showLinkDialog && (
        <TextInputDialog
          title={t('comment_link_title', 'Insert link')}
          label={t('comment_link_label', 'Web address')}
          placeholder="https://"
          confirmLabel={t('comment_link_confirm', 'Insert')}
          maxLength={2048}
          onConfirm={applyLink}
          onCancel={() => { setShowLinkDialog(false); editor?.commands.focus(); }}
        />
      )}
    </div>
  );
};

export default RichTextField;
